class CosumyShippingProgressbar {
  constructor(element) {
    this.element = element;
    this.fillElement = element.querySelector('.cosumy-progressbar__fill');
    this.textElement = element.querySelector('.cosumy-progressbar__text');
    this.shippingIcon = element.querySelector('.cosumy-progressbar__icon--shipping');
    this.giftIcon = element.querySelector('.cosumy-progressbar__icon--gift');
    
    this.shippingThreshold = 3900; // 39€ in cents
    this.giftThreshold = 7900; // 79€ in cents
    
    this.currentProgress = 0;
    this.targetProgress = 0;
    this.animationFrame = null;
    
    this.init();
  }

  init() {
    this.updateProgressbar();
    
    // Listen for cart updates
    document.addEventListener('cart:updated', () => {
      this.updateProgressbar();
    });
  }

  getCurrentCartTotal() {
    // Get cart total from Shopify cart object or DOM
    const cartTotalElement = document.querySelector('[data-cart-total]');
    if (cartTotalElement) {
      return parseInt(cartTotalElement.dataset.cartTotal) || 0;
    }
    
    // Fallback: try to get from window.cart if available
    if (window.cart && window.cart.total_price) {
      return window.cart.total_price;
    }
    
    return 0;
  }

  updateProgressbar() {
    const cartTotal = this.getCurrentCartTotal();
    
    // Calculate progress percentage based on 39€ = 50%, 79€ = 100%
    let progress = 0;
    if (cartTotal >= this.giftThreshold) {
      progress = 100;
    } else if (cartTotal >= this.shippingThreshold) {
      // Between 39€ and 79€: progress from 50% to 100%
      const progressBeyondShipping = (cartTotal - this.shippingThreshold) / (this.giftThreshold - this.shippingThreshold);
      progress = 50 + (progressBeyondShipping * 50);
    } else {
      // Below 39€: progress from 0% to 50%
      progress = (cartTotal / this.shippingThreshold) * 50;
    }

    // Ensure minimum progress for visual feedback
    if (cartTotal > 0 && progress < 8) {
      progress = 8;
    }

    // Update progress bar fill with CSS transition (600ms ease-out)
    this.fillElement.style.width = `${Math.min(progress, 100)}%`;

    // Update icons and text
    this.updateIconsAndText(cartTotal);
  }

  updateIconsAndText(cartTotal) {
    const shippingReached = cartTotal >= this.shippingThreshold;
    const giftReached = cartTotal >= this.giftThreshold;

    // Update shipping icon with smooth transition
    if (shippingReached && !this.shippingIcon.dataset.filled) {
      this.shippingIcon.dataset.filled = 'true';
      this.shippingIcon.classList.add('cosumy-progressbar__icon--active');
      setTimeout(() => this.updateShippingIcon(true), 100);
      setTimeout(() => {
        this.shippingIcon.classList.remove('cosumy-progressbar__icon--active');
      }, 1000);
    } else if (!shippingReached && this.shippingIcon.dataset.filled) {
      this.shippingIcon.dataset.filled = 'false';
      this.updateShippingIcon(false);
    } else if (!this.shippingIcon.dataset.filled) {
      this.updateShippingIcon(shippingReached);
    }

    // Update gift icon with smooth transition
    if (giftReached && !this.giftIcon.dataset.filled) {
      this.giftIcon.dataset.filled = 'true';
      this.giftIcon.classList.add('cosumy-progressbar__icon--active');
      setTimeout(() => this.updateGiftIcon(true), 100);
      setTimeout(() => {
        this.giftIcon.classList.remove('cosumy-progressbar__icon--active');
      }, 1000);
    } else if (!giftReached && this.giftIcon.dataset.filled) {
      this.giftIcon.dataset.filled = 'false';
      this.updateGiftIcon(false);
    } else if (!this.giftIcon.dataset.filled) {
      this.updateGiftIcon(giftReached);
    }

    // Update text with smooth transition
    this.updateText(cartTotal, shippingReached, giftReached);
  }

  updateShippingIcon(filled) {
    const svg = filled ? this.getShippingIconFilled() : this.getShippingIconOutline();
    this.shippingIcon.innerHTML = svg;
  }

  updateGiftIcon(filled) {
    const svg = filled ? this.getGiftIconFilled() : this.getGiftIconOutline();
    this.giftIcon.innerHTML = svg;
  }

  updateText(cartTotal, shippingReached, giftReached) {
    let text = '';
    
    if (giftReached) {
      text = '🎉 Du erhältst kostenlosen <strong>Versand</strong> und unsere <strong>Milchkännchen</strong>-Zugabe – mit Liebe verpackt';
    } else if (shippingReached) {
      const remaining = this.giftThreshold - cartTotal;
      const remainingFormatted = this.formatPrice(remaining);
      text = `<strong>Versand</strong> ist jetzt kostenlos – nur noch <strong>${remainingFormatted}</strong> bis zur <strong>Milchkännchen</strong>-Zugabe`;
    } else {
      const remaining = this.shippingThreshold - cartTotal;
      const remainingFormatted = this.formatPrice(remaining);
      text = `Nur noch <strong>${remainingFormatted}</strong> bis zum kostenlosen <strong>Versand</strong>`;
    }

    // Smooth text transition
    if (this.textElement.innerHTML !== text) {
      this.textElement.style.opacity = '0.7';
      setTimeout(() => {
        this.textElement.innerHTML = text;
        this.textElement.style.opacity = '1';
      }, 200);
    }
  }

  formatPrice(cents) {
    return (cents / 100).toFixed(2).replace('.', ',') + ' €';
  }

  getShippingIconOutline() {
    return `<svg viewBox="0 0 24 24" fill="none" stroke="#433E3F" stroke-width="2">
      <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/>
      <path d="m3.3 7 8.7 5 8.7-5"/>
      <path d="M12 22V12"/>
    </svg>`;
  }

  getShippingIconFilled() {
    return `<svg viewBox="0 0 24 24" fill="#584F2A" stroke="#584F2A" stroke-width="2">
      <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/>
      <path d="m3.3 7 8.7 5 8.7-5" stroke="#EDEAE4"/>
      <path d="M12 22V12" stroke="#EDEAE4"/>
    </svg>`;
  }

  getGiftIconOutline() {
    return `<svg viewBox="0 0 24 24" fill="none" stroke="#433E3F" stroke-width="2">
      <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"/>
      <path d="M3 6h18"/>
      <path d="M16 10a4 4 0 0 1-8 0"/>
    </svg>`;
  }

  getGiftIconFilled() {
    return `<svg viewBox="0 0 24 24" fill="#584F2A" stroke="#584F2A" stroke-width="2">
      <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"/>
      <path d="M3 6h18" stroke="#EDEAE4"/>
      <path d="M16 10a4 4 0 0 1-8 0" stroke="#EDEAE4"/>
    </svg>`;
  }
}

// Initialize progress bars when DOM is loaded
document.addEventListener('DOMContentLoaded', function() {
  const progressbars = document.querySelectorAll('.cosumy-progressbar');
  progressbars.forEach(progressbar => {
    new CosumyShippingProgressbar(progressbar);
  });
});

// Re-initialize after AJAX cart updates
document.addEventListener('shopify:section:load', function() {
  const progressbars = document.querySelectorAll('.cosumy-progressbar');
  progressbars.forEach(progressbar => {
    new CosumyShippingProgressbar(progressbar);
  });
});